import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    MenuItem,
    Select,
    IconButton,
    Alert,
    Divider,
    CircularProgress,
    Stack,
    Chip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckIcon,
    Translate as TranslateIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import SignatureModalWithAuto from '../common/SignatureModalWithAuto';
import tempExhibitBService from '../../services/tempExhibitBService';
import { initializeDB } from '../../config/db';
import { TEMP_AGENCY_STORE, TEMPS_STORE } from '../../config/constants';
import { TENANT_LEGAL_NAME, TENANT_LEGAL_NAME_SHORT } from '../../config/tenant';

// English text template
const ENGLISH_TEXT = `I, the undersigned, an employee of the _AGENCY_ (hereinafter "_AGENCY_"), agree to accept a temporary work assignment at the premises operated by the ${TENANT_LEGAL_NAME} (hereafter "${TENANT_LEGAL_NAME_SHORT}"). As a condition of my assignment, I acknowledge the following:

1. I am an employee of _AGENCY_ and _AGENCY_ is my employer for all purposes. I acknowledge that I am not an employee of ${TENANT_LEGAL_NAME_SHORT} for any purpose. _AGENCY_ is a temporary employment agency which is an independent contractor of ${TENANT_LEGAL_NAME_SHORT}. As an employee of _AGENCY_, I am neither covered by, nor do I have rights in, personnel policies, procedures, and practices established by ${TENANT_LEGAL_NAME_SHORT} for its employees.

2. As an employee of _AGENCY_ and not of ${TENANT_LEGAL_NAME_SHORT}, I am not entitled to participate in any benefit plan, fringe benefit, or pay practice that ${TENANT_LEGAL_NAME_SHORT} has or may hereafter establish for its employees.

3. The _AGENCY_ will make payment to me directly for services rendered and withhold taxes from my pay.

4. Only the _AGENCY_ may address any questions or problems I might have about my compensation and benefits.

5. I will direct any problems or complaints I may have regarding the terms of my assignment to the _AGENCY_.

6. I acknowledge that I am not covered by ${TENANT_LEGAL_NAME_SHORT}'s worker's compensation insurance policies, and I am not entitled to worker's compensation coverage by ${TENANT_LEGAL_NAME_SHORT}. I will follow all safety procedures and ask for help should I have questions regarding safety procedures. In the event I am injured as a result of this work assignment, in consideration of my assignment to ${TENANT_LEGAL_NAME_SHORT}, I will look only to the _AGENCY_ for payment of medical bills, loss of wages, or other damages sustained by me or my family, and hereby agree to waive, release, and hold harmless ${TENANT_LEGAL_NAME_SHORT} and its shareholders, officers, directors, employees, insurers, and agents from any such claims.

7. I understand this is a temporary assignment that will continue or not continue solely at the discretion of ${TENANT_LEGAL_NAME_SHORT}, and I agree to hold ${TENANT_LEGAL_NAME_SHORT} harmless from any claims arising out of the discontinuance of this assignment for any reason.

8. This "Acknowledgement of Status and Hold Harmless" will not constitute, or be construed as, an employment contract between ${TENANT_LEGAL_NAME_SHORT} and me.

9. I hereby represent that I am eighteen (18) years of age or older. The terms of this "Acknowledgement of Status and Waiver, Release, and Hold Harmless" may not be amended except in writing and signed by an authorized representative of ${TENANT_LEGAL_NAME_SHORT}. Verbal understandings have no effect. This Acknowledgement of Status and Waiver, Release, and Hold Harmless constitutes the full and complete understanding between ${TENANT_LEGAL_NAME_SHORT} and me and supersedes any and all prior oral or written understandings between us, with respect to the subject matter herein.

10. I agree that ${TENANT_LEGAL_NAME_SHORT}, whenever stated in this Agreement, includes all related entities owned in whole or in part by, or controlled by owners of ${TENANT_LEGAL_NAME_SHORT}.`;

// Spanish text template
const SPANISH_TEXT = `Yo, el abajo firmante, empleado de _AGENCY_ (en adelante "_AGENCY_"), acepto aceptar una asignación de trabajo temporal en las instalaciones operadas por ${TENANT_LEGAL_NAME} (en adelante "${TENANT_LEGAL_NAME_SHORT}"). Como condición de mi asignación, reconozco lo siguiente:

1. Soy empleado de _AGENCY_ y _AGENCY_ es mi empleador para todos los propósitos. Reconozco que no soy empleado de ${TENANT_LEGAL_NAME_SHORT} para ningún propósito. _AGENCY_ es una agencia de empleo temporal que es un contratista independiente de ${TENANT_LEGAL_NAME_SHORT}. Como empleado de _AGENCY_, no estoy cubierto ni tengo derechos en las políticas, procedimientos y prácticas de personal establecidas por ${TENANT_LEGAL_NAME_SHORT} para sus empleados.

2. Como empleado de _AGENCY_ y no de ${TENANT_LEGAL_NAME_SHORT}, no tengo derecho a participar en ningún plan de beneficios, beneficio adicional o práctica de pago que ${TENANT_LEGAL_NAME_SHORT} tenga o pueda establecer en el futuro para sus empleados.

3. _AGENCY_ me pagará directamente por los servicios prestados y retendrá impuestos de mi pago.

4. Solo _AGENCY_ puede abordar cualquier pregunta o problema que pueda tener sobre mi compensación y beneficios.

5. Dirigiré cualquier problema o queja que pueda tener con respecto a los términos de mi asignación a _AGENCY_.

6. Reconozco que no estoy cubierto por las pólizas de seguro de compensación laboral de ${TENANT_LEGAL_NAME_SHORT}, y no tengo derecho a la cobertura de compensación laboral por parte de ${TENANT_LEGAL_NAME_SHORT}. Seguiré todos los procedimientos de seguridad y pediré ayuda si tengo preguntas sobre los procedimientos de seguridad. En caso de que resulte lesionado como resultado de esta asignación de trabajo, en consideración de mi asignación a ${TENANT_LEGAL_NAME_SHORT}, buscaré solo a _AGENCY_ para el pago de facturas médicas, pérdida de salarios u otros daños sufridos por mí o mi familia, y por la presente acepto renunciar, liberar y eximir de responsabilidad a ${TENANT_LEGAL_NAME_SHORT} y sus accionistas, funcionarios, directores, empleados, aseguradores y agentes de dichos reclamos.

7. Entiendo que esta es una asignación temporal que continuará o no continuará únicamente a discreción de ${TENANT_LEGAL_NAME_SHORT}, y acepto eximir a ${TENANT_LEGAL_NAME_SHORT} de cualquier reclamo que surja de la interrupción de esta asignación por cualquier motivo.

8. Este "Reconocimiento de Estado y Exención de Responsabilidad" no constituirá, ni se interpretará como, un contrato de trabajo entre ${TENANT_LEGAL_NAME_SHORT} y yo.

9. Por la presente declaro que tengo dieciocho (18) años de edad o más. Los términos de este "Reconocimiento de Estado y Renuncia, Liberación y Exención de Responsabilidad" no pueden ser modificados excepto por escrito y firmados por un representante autorizado de ${TENANT_LEGAL_NAME_SHORT}. Los entendimientos verbales no tienen efecto. Este Reconocimiento de Estado y Renuncia, Liberación y Exención de Responsabilidad constituye el entendimiento completo y total entre ${TENANT_LEGAL_NAME_SHORT} y yo y reemplaza todos y cada uno de los entendimientos orales o escritos previos entre nosotros, con respecto al tema aquí tratado.

10. Acepto que ${TENANT_LEGAL_NAME_SHORT}, cuando se indique en este Acuerdo, incluye todas las entidades relacionadas que son propiedad total o parcial de, o están controladas por los propietarios de ${TENANT_LEGAL_NAME_SHORT}.`;

const TempExhibitB = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const sigPadRef = useRef();
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [agencies, setAgencies] = useState([]);
    const [temps, setTemps] = useState([]);
    const [filteredTemps, setFilteredTemps] = useState([]);
    const [agencyAllCompleted, setAgencyAllCompleted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    
    // Ref for scrollable content
    const scrollableRef = useRef(null);
    
    // Form state
    const [formData, setFormData] = useState({
        // id will be added after first save
        language: 'english',
        agencyId: '',
        agencyName: '',
        employeeName: '',
        employeeId: '',
        signature: '',
        status: 'draft',
        signedDate: new Date()
    });
    
    // Auto-save timer ref
    const autoSaveTimerRef = useRef(null);
    
    // Check if user has scrolled to the end
    const handleScroll = (e) => {
        const element = e.target;
        const scrolledToBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 10;
        if (scrolledToBottom && !hasScrolledToEnd) {
            setHasScrolledToEnd(true);
        }
    };

    // Load agencies, temps and form data on mount
    useEffect(() => {
        loadAgencies();
        loadTemps();
        if (id) {
            loadForm(id);
        }
    }, [id]);

    // Helper to normalize agency strings (trim/collapse spaces)
    const normalizeAgency = (s) => (s || '').toString().trim().replace(/\s+/g, ' ');

    // Filter temps by selected agency and sort alphabetically (exclude already signed)
    useEffect(() => {
        if (formData.agencyName && temps.length > 0) {
            const selected = normalizeAgency(formData.agencyName);
            const agencyTemps = temps.filter(temp => normalizeAgency(temp.agency) === selected);
            const filtered = agencyTemps.filter(temp => temp.bHoldHarmless !== true);
            // Sort by fullName field alphabetically
            filtered.sort((a, b) => {
                const nameA = (a.fullName || '').toLowerCase();
                const nameB = (b.fullName || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
            setFilteredTemps(filtered);
            setAgencyAllCompleted(agencyTemps.length > 0 && filtered.length === 0);
        } else {
            setFilteredTemps([]);
            setAgencyAllCompleted(false);
        }
    }, [formData.agencyName, temps]);
    
    // Auto-save on form data changes
    useEffect(() => {
        // Clear existing timer
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        
        // Don't auto-save if form is empty or completed
        if (!formData.agencyId && !formData.employeeName) return;
        if (formData.status === 'completed') return;
        
        // Set new timer for auto-save after 1 second of no changes
        autoSaveTimerRef.current = setTimeout(() => {
            handleAutoSave();
        }, 1000);
        
        // Cleanup on unmount
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [formData]);

    const loadAgencies = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMP_AGENCY_STORE, 'readonly');
            const store = tx.objectStore(TEMP_AGENCY_STORE);
            const allAgencies = await store.getAll();
            
            // Filter and get data properly
            const activeAgencies = allAgencies
                .filter(a => a && !a.inActive)
                .map(a => ({
                    id: a.id,
                    agency: a.agency || a.Agency || '',
                    notes: a.notes || a.Notes || '',
                    po: a.po || a.PO || '',
                    status: a.status !== false
                }))
                .filter(a => a.agency) // Only include agencies with names
                .sort((a, b) => a.agency.toLowerCase().localeCompare(b.agency.toLowerCase())); // Sort alphabetically
            
            setAgencies(activeAgencies);
        } catch (error) {
            console.error('Error loading agencies:', error);
            setErrorMessage('Failed to load agencies');
        }
    };

    const loadTemps = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(TEMPS_STORE, 'readonly');
            const store = tx.objectStore(TEMPS_STORE);
            const allTemps = await store.getAll();

            // Helper: interpret only explicit TRUE values as true
            const isExplicitTrue = (v) => {
                if (v === true) return true;
                if (v === 1) return true;
                if (typeof v === 'string') {
                    const s = v.trim().toLowerCase();
                    return s === 'true' || s === '1' || s === 'yes' || s === 'y';
                }
                return false;
            };

            // Filter and get active temps (preserve bHoldHarmless flag strictly)
            const activeTemps = allTemps
                .filter(t => t && (t.status !== false))
                .map(t => ({
                    id: t.id,
                    fullName: t.FullName || t.fullName || t.Name || '',
                    agency: t.Agency || t.agency || '',
                    employeeId: t.EmployeeId || t.employeeId || t.ID || '',
                    // Only treat explicit TRUE/1/'true' as true. Null/0/''/'false' stay false
                    bHoldHarmless: isExplicitTrue(t.bHoldHarmless ?? t.BHoldHarmless)
                }))
                .filter(t => t.fullName); // Only include temps with names
            
            setTemps(activeTemps);
        } catch (error) {
            console.error('Error loading temps:', error);
            setErrorMessage('Failed to load temporary employees');
        }
    };

    const loadForm = async (formId) => {
        setLoading(true);
        try {
            const form = await tempExhibitBService.getFormById(parseInt(formId));
            if (form) {
                setFormData(form);
                // If form is completed, disable editing
                if (form.status === 'completed') {
                    setErrorMessage('This form has been completed and cannot be edited');
                }
            } else {
                setErrorMessage('Form not found');
            }
        } catch (error) {
            console.error('Error loading form:', error);
            setErrorMessage('Failed to load form');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        // Prevent editing if form is completed
        if (formData.status === 'completed') {
            setErrorMessage('This form has been completed and cannot be edited');
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Update agency name when agency is selected
        if (field === 'agencyId') {
            const selectedAgency = agencies.find(a => a.id === value);
            if (selectedAgency) {
                setFormData(prev => ({
                    ...prev,
                    agencyId: value,
                    agencyName: (selectedAgency.agency || '').trim(),
                    // Reset employee selection when agency changes
                    employeeName: '',
                    employeeId: ''
                }));
            }
            // Reset scroll state when agency changes
            setHasScrolledToEnd(false);
        }
        
        // Handle employee selection
        if (field === 'employeeId') {
            const selectedTemp = filteredTemps.find(t => t.id === value);
            if (selectedTemp) {
                setFormData(prev => ({
                    ...prev,
                    employeeId: selectedTemp.employeeId,
                    employeeName: selectedTemp.fullName
                }));
            }
            // Reset scroll state when employee changes
            setHasScrolledToEnd(false);
        }
        
        // Reset scroll state when employee name changes
        if (field === 'employeeName') {
            setHasScrolledToEnd(false);
        }
    };

    const handleSignatureAccept = async () => {
        if (sigPadRef.current) {
            const signatureData = sigPadRef.current.toDataURL();
            
            // Update form data with signature
            const updatedFormData = {
                ...formData,
                signature: signatureData
            };
            
            setFormData(updatedFormData);
            setSignatureModalOpen(false);
            
            // Automatically submit the form after signature is added
            // Use the updated form data directly instead of waiting for state
            setTimeout(() => {
                handleSaveWithData(updatedFormData, 'completed');
            }, 100);
        }
    };

    const handleAutoSave = async () => {
        if (!formData.agencyId && !formData.employeeName) return;
        if (formData.status === 'completed') return; // Don't auto-save completed forms
        
        try {
            const dataToSave = {
                ...formData,
                status: 'draft',
                signedDate: new Date().toISOString()
            };
            
            const result = await tempExhibitBService.saveForm(dataToSave);
            if (result.success && result.data && result.data.id) {
                // Update form ID so we keep updating the same form
                if (!formData.id) {
                    setFormData(prev => ({
                        ...prev,
                        id: result.data.id
                    }));
                }
            }
        } catch (error) {
            console.error('Auto-save error:', error);
        }
    };

    const handleSaveWithData = async (dataToUse, status = 'draft') => {
        // Validation
        if (!dataToUse.agencyId) {
            setErrorMessage('Please select an agency');
            return;
        }
        if (!dataToUse.employeeName) {
            setErrorMessage('Please enter employee name');
            return;
        }
        if (status === 'completed' && !dataToUse.signature) {
            setErrorMessage('Please provide signature to complete the form');
            return;
        }

        setSaving(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const dataToSave = {
                ...dataToUse,
                status,
                signedDate: new Date().toISOString(),
                syncStatus: 'pending' // Explicitly set pending status
            };

            const result = await tempExhibitBService.saveForm(dataToSave);
            
            if (result.success) {
                // Update formData with the saved ID
                if (result.data && result.data.id && !dataToUse.id) {
                    setFormData(prev => ({
                        ...prev,
                        id: result.data.id
                    }));
                }
                
                // If completed, also submit to server
                if (status === 'completed') {
                    setSuccessMessage('Submitting form to server...');
                    // Use the saved data for server submission
                    const serverResult = await tempExhibitBService.submitToServer(result.data || dataToSave);
                    
                    if (serverResult.success) {
                        setSuccessMessage('Form submitted successfully! PDF generated.');
                        
                        // Update form with server ID
                        setFormData(prev => ({
                            ...prev,
                            serverId: serverResult.serverId,
                            status: 'completed'
                        }));
                        
                        setTimeout(() => {
                            navigate('/temps/exhibit-b/list');
                        }, 2000);
                    } else {
                        // Form saved locally but failed to submit to server
                        setErrorMessage('Form saved locally but could not be submitted to server. You can retry from the forms list.');
                        
                        // Navigate to list after a delay
                        setTimeout(() => {
                            navigate('/temps/exhibit-b/list');
                        }, 3000);
                    }
                } else {
                    setSuccessMessage('Form saved as draft');
                }
            } else {
                setErrorMessage('Failed to save form');
            }
        } catch (error) {
            console.error('Error saving form:', error);
            setErrorMessage('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (status = 'draft') => {
        // Use the current formData
        return handleSaveWithData(formData, status);
    };

    const handleRetrySubmit = async () => {
        setIsRetrying(true);
        setErrorMessage('');
        setSuccessMessage('');
        
        try {
            const result = await tempExhibitBService.submitToServer(formData);
            
            if (result.success) {
                setSuccessMessage('Form successfully submitted to server!');
                
                // Update form with server ID, synced status AND completed status
                const updatedForm = {
                    ...formData,
                    status: 'completed', // ВАЖНО: меняем статус на completed!
                    serverId: result.serverId,
                    syncStatus: 'synced',
                    syncedAt: new Date().toISOString()
                };
                
                await tempExhibitBService.saveForm(updatedForm);
                setFormData(updatedForm);
                
                // Navigate to list after successful submit
                setTimeout(() => {
                    navigate('/temps/exhibit-b/list');
                }, 2000);
            } else {
                setErrorMessage(`Failed to submit: ${result.error}`);
            }
        } catch (error) {
            console.error('Retry error:', error);
            setErrorMessage('Failed to submit to server');
        } finally {
            setIsRetrying(false);
        }
    };

    // Format the agreement text with replacements
    const getFormattedText = () => {
        const text = formData.language === 'english' ? ENGLISH_TEXT : SPANISH_TEXT;
        const agencyName = formData.agencyName || '_______________';
        
        // Replace agency name and make it bold, also make company abbreviation bold
        return text
            .replace(/_AGENCY_/g, `<strong>${agencyName}</strong>`)
            .replace(new RegExp(TENANT_LEGAL_NAME_SHORT, 'g'), `<strong>${TENANT_LEGAL_NAME_SHORT}</strong>`);
    };

    // Format date
    const getFormattedDate = () => {
        const date = new Date();
        const day = date.getDate();
        const year = date.getFullYear();
        
        if (formData.language === 'english') {
            const month = format(date, 'MMMM');
            return `${day} day of ${month}, ${year}`;
        } else {
            const month = format(date, 'MMMM', { locale: es });
            return `${day} día de ${month}, ${year}`;
        }
    };

    // Labels based on language
    const labels = {
        english: {
            title: 'TEMPS EXHIBIT B ACKNOWLEDGEMENT',
            language: 'Language',
            agency: 'Select Agency',
            name: 'Employee Name',
            agreedTo: 'Agreed to this',
            printName: 'Please print',
            nameOfTemp: 'Name of Temporary Employee',
            signature: 'Signature',
            signatureOf: 'Signature of printed name above',
            dhgNote: `* ${TENANT_LEGAL_NAME_SHORT} as indicated in this document is ${TENANT_LEGAL_NAME}.`,
            addSignature: 'Sign & Submit',
            reSign: 'Re-sign & Submit',
            scrollToEnd: 'Please read the entire agreement to enable signature',
            complete: 'Complete & Sign'
        },
        spanish: {
            title: 'ANEXO B DE TEMPS RECONOCIMIENTO',
            language: 'Idioma',
            agency: 'Seleccionar Agencia',
            name: 'Nombre del Empleado',
            agreedTo: 'Acordado en este',
            printName: 'Por favor imprima',
            nameOfTemp: 'Nombre del Empleado Temporal',
            signature: 'Firma',
            signatureOf: 'Firma del nombre impreso arriba',
            dhgNote: `* ${TENANT_LEGAL_NAME_SHORT} como se indica en este documento es ${TENANT_LEGAL_NAME}.`,
            addSignature: 'Firmar y Enviar',
            reSign: 'Volver a firmar y Enviar',
            scrollToEnd: 'Por favor lea todo el acuerdo para habilitar la firma',
            complete: 'Completar y Firmar'
        }
    };

    const t = labels[formData.language];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            height: 'calc(100vh - 60px)', // Учитываем высоту главного header
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <Box sx={{ 
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 2,
                flexShrink: 0
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <IconButton onClick={() => navigate('/temps/exhibit-b/list')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight="600">
                        {t.title}
                    </Typography>
                </Box>

                {/* Language Selection */}
                <RadioGroup
                    row
                    value={formData.language}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={formData.status === 'completed'}
                >
                    <FormControlLabel value="english" control={<Radio size="small" disabled={formData.status === 'completed'} />} label="English" />
                    <FormControlLabel value="spanish" control={<Radio size="small" disabled={formData.status === 'completed'} />} label="Español" />
                </RadioGroup>

                {/* Agency and Name Selection */}
                <Stack direction="row" spacing={2}>
                    <TextField
                        fullWidth
                        select
                        size="small"
                        label={t.agency}
                        value={formData.agencyId}
                        onChange={(e) => handleInputChange('agencyId', e.target.value)}
                        disabled={formData.status === 'completed'}
                    >
                        <MenuItem value="">
                            <em>{t.agency}</em>
                        </MenuItem>
                        {agencies.map(agency => (
                            <MenuItem key={agency.id} value={agency.id}>
                                {agency.agency}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        select
                        size="small"
                        label={t.name}
                        value={filteredTemps.find(t => t.fullName === formData.employeeName)?.id || ''}
                        onChange={(e) => handleInputChange('employeeId', e.target.value)}
                        disabled={formData.status === 'completed' || !formData.agencyId}
                        helperText={!formData.agencyId
                            ? 'Please select an agency first'
                            : (filteredTemps.length === 0 && formData.agencyId
                                ? (agencyAllCompleted ? 'All temps in this agency have already completed the form' : 'No employees found for this agency')
                                : '')}
                    >
                        <MenuItem value="">
                            <em>{t.name}</em>
                        </MenuItem>
                        {filteredTemps.map(temp => (
                            <MenuItem key={temp.id} value={temp.id}>
                                {temp.fullName} {temp.employeeId ? `(${temp.employeeId})` : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ 
                p: 2, 
                flex: 1, 
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0 // Important for Firefox
            }}>
                {/* Agreement Text - Only show if agency and name are filled */}
                {formData.agencyId && formData.employeeName ? (
                    <Box 
                        ref={scrollableRef}
                        onScroll={handleScroll}
                        sx={{ 
                            backgroundColor: 'white',
                            p: 2,
                            mb: 2,
                            borderRadius: 1,
                            maxHeight: '50vh',
                            minHeight: '200px',
                            overflow: 'auto',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <Typography 
                            variant="body2" 
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                            dangerouslySetInnerHTML={{ __html: getFormattedText() }}
                        />
                    </Box>
                ) : (
                    <Box sx={{ 
                        backgroundColor: 'white',
                        p: 4,
                        mb: 2,
                        borderRadius: 1,
                        textAlign: 'center',
                        color: 'text.secondary'
                    }}>
                        <Typography variant="body2">
                            {formData.language === 'english' 
                                ? 'Please select an agency and enter employee name to view the agreement'
                                : 'Por favor seleccione una agencia e ingrese el nombre del empleado para ver el acuerdo'
                            }
                        </Typography>
                    </Box>
                )}

                {/* Signature Section */}
                <Box sx={{ 
                    backgroundColor: 'white',
                    p: 2,
                    borderRadius: 1,
                    flexShrink: 0
                }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {t.agreedTo} <strong>{getFormattedDate()}</strong>
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Name and Signature in one row */}
                    <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t.printName}:
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {formData.employeeName || '_______________________________'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({t.nameOfTemp})
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t.signature}:
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {formData.signature ? (
                                    <Box>
                                        <Box sx={{ 
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                            p: 1,
                                            backgroundColor: '#fafafa',
                                            display: 'inline-block'
                                        }}>
                                            <img 
                                                src={formData.signature} 
                                                alt="Signature" 
                                                style={{ maxHeight: 60, display: 'block' }}
                                            />
                                        </Box>
                                        <Button 
                                            size="small" 
                                            onClick={() => setSignatureModalOpen(true)}
                                            sx={{ mt: 1 }}
                                            disabled={!hasScrolledToEnd || formData.status === 'completed'}
                                        >
                                            {t.reSign}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Button 
                                            variant="outlined" 
                                            size="small"
                                            onClick={() => setSignatureModalOpen(true)}
                                            disabled={!hasScrolledToEnd || !formData.agencyId || !formData.employeeName || formData.status === 'completed'}
                                        >
                                            {t.addSignature}
                                        </Button>
                                        {!hasScrolledToEnd && formData.agencyId && formData.employeeName && (
                                            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                                {t.scrollToEnd}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                ({t.signatureOf})
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Retry Submit Button for draft forms with signature */}
                    {formData.status === 'draft' && formData.signature && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={handleRetrySubmit}
                                disabled={isRetrying}
                                startIcon={isRetrying ? <CircularProgress size={20} /> : <RefreshIcon />}
                            >
                                {isRetrying ? 'Submitting to Server...' : 'Retry Submit to Server'}
                            </Button>
                            {formData.syncError && (
                                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                    Last error: {formData.syncError}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ 
                        mt: 3, 
                        p: 2, 
                        backgroundColor: '#f5f5f5',
                        borderLeft: '3px solid #1976d2',
                        borderRadius: 1
                    }}>
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ fontStyle: 'italic' }}
                            dangerouslySetInnerHTML={{ __html: t.dhgNote.replace(new RegExp(TENANT_LEGAL_NAME_SHORT, 'g'), `<strong>${TENANT_LEGAL_NAME_SHORT}</strong>`).replace(/\*/g, '<sup>*</sup>') }}
                        />
                    </Box>

                    {/* Complete Button removed - form auto-submits after signature */}
                    
                    {/* Show completed status */}
                    {formData.status === 'completed' && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Chip 
                                icon={<CheckIcon />} 
                                label="Form Completed" 
                                color="success" 
                                size="large"
                            />
                        </Box>
                    )}
                </Box>

                {/* Messages */}
                {successMessage && (
                    <Alert severity="success" sx={{ mt: 2, flexShrink: 0 }} onClose={() => setSuccessMessage('')}>
                        {successMessage}
                    </Alert>
                )}
                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2, flexShrink: 0 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}
            </Box>

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={formData.employeeName}
                onAccept={handleSignatureAccept}
            />
        </Box>
    );
};

export default TempExhibitB;
